/* eslint-disable no-console */
import Auth, { CognitoUser } from "@aws-amplify/auth";
import { useLocalStorage } from "@gravitywelluk/react-hooks";
import { Hub } from "@aws-amplify/core";
import { useToast } from "@chakra-ui/react";
import React from "react";

import {
  User,
  useUsersApi
} from "../api";

interface AuthDataState {
  authenticated: boolean;
  error: string | null;
  authenticating: boolean;
  fetchingUser?: boolean;
  sub?: string;
  user?: User,
  idToken: string;
}

// authenticating: true on initial load until auth check complete
const initialAuthContext: AuthDataState = {
  authenticated: false,
  error: null,
  authenticating: true,
  fetchingUser: false,
  idToken: ""
};

export interface AuthContextAttributes {
  authDataState: AuthDataState;
  setAuthDataState: (newState: AuthDataState) => void;
}

export const AuthContext = React.createContext<AuthContextAttributes>({
  authDataState: initialAuthContext,
  setAuthDataState: () => null
});

const getToken = (user: CognitoUser) => user.getSignInUserSession()?.getIdToken().getJwtToken() || "";

export const AuthProvider: React.FC = ({ children }) => {
  const [ authDataState, setAuthDataState ] = useLocalStorage<AuthDataState>("authDataState", initialAuthContext);
  const [ getMeRes, getMeReq ] = useUsersApi("GET_ME");
  const toast = useToast();

  // Check if the user is authenticated on initial app load
  React.useEffect(() => {
    Auth.currentAuthenticatedUser().then(user => {
      setAuthDataState({
        authenticated: true,
        error: null,
        authenticating: false,
        idToken: user.signInUserSession.getIdToken().getJwtToken()
      });

      getMeReq(
        { cognitoId: user.attributes.sub },
        { headers: { Authorization: `Bearer ${user.signInUserSession.getIdToken().getJwtToken()}` } }
      );
    }).catch(err => (
      setAuthDataState({
        authenticated: false,
        error: err,
        authenticating: false,
        idToken: ""
      })
    ));
  }, [ getMeReq, setAuthDataState ]);

  React.useEffect(() => {
    // listen to Amplify Auth state
    Hub.listen("auth", ({ payload: { event, data } }) => {
      switch (event) {
        case "signIn":

          setAuthDataState({
            authenticated: true,
            error: null,
            authenticating: false,
            sub: data.attributes.sub,
            idToken: getToken(data)
          });

          getMeReq(
            { cognitoId: data.attributes.sub },
            { headers: { Authorization: `Bearer ${getToken(data)}` } }
          );

          break;
        case "signOut":
          setAuthDataState({
            authenticated: false,
            error: null,
            authenticating: false,
            user: undefined,
            idToken: ""
          });
          break;
        case "signIn_failure":
          setAuthDataState({
            authenticated: false,
            error: data,
            authenticating: false,
            user: undefined,
            idToken: ""
          });
          break;
        case "signUp":
        case "configured":
        default:
          // Do nothing
          // - configured not supported.
          // - signUp not supported.
          break;
      }
    });
  }, [ getMeReq, setAuthDataState ]);

  React.useEffect(() => {
    if (getMeRes.error) {
      toast({
        status: "error",
        title: "Couldn't get user",
        description: "There was a problem getting your user information."
      });
      // Logout
      Auth.signOut();
    }

    if (getMeRes.data) {
      setAuthDataState(currentState => ({
        ...currentState,
        fetchingUser: false,
        user: getMeRes.data ? getMeRes.data : undefined
      }));
    }
  }, [
    getMeRes,
    toast,
    setAuthDataState
  ]);

  const contextValue: AuthContextAttributes = React.useMemo(() => ({
    authDataState,
    setAuthDataState
  }), [ authDataState, setAuthDataState ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};